import React from "react";
import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import { IconButton, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import Button from "components/atoms/Button";
import { CoverageAreaProps } from "store/modules/Logistics/types.store";
import { DealerUsersProps } from "store/modules/DealerUsers/types.store";
import MapDriversInlineForm from "../MapDriversInlineForm";
import * as Shared from "../../styles";
import * as Styled from "./styles";
import {
  deliveryInfoIconInnerSx,
  deliveryListShippingIconSx,
  deliveryPeopleInfoIconSx,
  getDeliveryPeoplePriorityTooltipComponentsProps,
} from "./muiSx";
import { driverOptionFromEntry } from "components/CardDriverArea/coverageAreaDrivers";
import { useCardDriverAreaMap } from "./useCardDriverAreaMap";
import {
  DriverEditCancelConfirmationModal,
  DriverEditSaveConfirmationModal,
} from "./components";

interface CardDriverAreaMapProps {
  dealerSelected: string;
  currentCoverageArea: CoverageAreaProps;
  dealerDrivers: DealerUsersProps[];
}

function CardDriverAreaMap({
  dealerSelected,
  currentCoverageArea,
  dealerDrivers,
}: CardDriverAreaMapProps) {
  const { t } = useTranslation();
  const {
    isEditing,
    slotRows,
    driverOptions,
    dealerDeliveryUsers,
    orderedDrivers,
    canAddSlot,
    hasDrivers,
    hasMultipleDrivers,
    primaryActionLabel,
    sectionTitle,
    editButtonSx,
    openEdit,
    requestSaveEdit,
    addEmptySlot,
    setSlotValue,
    removeSlot,
    isCancelModalOpen,
    requestCancelEdit,
    confirmCancelEdit,
    dismissCancelModal,
    isSaveModalOpen,
    confirmSaveEdit,
    dismissSaveModal,
  } = useCardDriverAreaMap({
    dealerSelected,
    currentCoverageArea,
    dealerDrivers,
  });

  return (
    <Shared.Container $isMapArea $hasAreaDriverAssigned={hasDrivers}>
      <Shared.TopContainer>
        <Typography variant="body1" fontWeight={600}>
          {sectionTitle}
        </Typography>
        <Tooltip
          title={t("deliveryPeoplePriorityTooltip")}
          componentsProps={getDeliveryPeoplePriorityTooltipComponentsProps()}
        >
          <IconButton
            size="small"
            aria-label={t("deliveryPeoplePriorityAriaLabel")}
            sx={deliveryPeopleInfoIconSx}
          >
            <InfoOutlinedIcon sx={deliveryInfoIconInnerSx} />
          </IconButton>
        </Tooltip>
      </Shared.TopContainer>

      {isEditing ? (
        <MapDriversInlineForm
          slotRows={slotRows}
          options={driverOptions}
          canAddSlot={canAddSlot}
          onSlotChange={setSlotValue}
          onAddSlot={addEmptySlot}
          onRemoveSlot={removeSlot}
          onSave={requestSaveEdit}
          onCancel={requestCancelEdit}
        />
      ) : (
        <Styled.MapAreaViewContainer $sideBySide={!hasMultipleDrivers}>
          <Styled.DriverList $fillRow={!hasMultipleDrivers}>
            {hasDrivers ? (
              orderedDrivers.map((driver, index) => {
                const { label } = driverOptionFromEntry(
                  driver,
                  dealerDeliveryUsers,
                );
                return (
                  <Styled.DriverListRow
                    key={`${driver.details.id}-${driver.priority}`}
                  >
                    <LocalShippingIcon sx={deliveryListShippingIconSx} />
                    <Styled.DriverListRowContent>
                      <Typography variant="caption" color="text.secondary">
                        {t("deliveryPersonSlotLabel", { number: index + 1 })}
                      </Typography>
                      <Typography variant="subtitle2">
                        {label || t("undefined")}
                      </Typography>
                    </Styled.DriverListRowContent>
                  </Styled.DriverListRow>
                );
              })
            ) : (
              <Styled.DriverListRow>
                <LocalShippingIcon sx={deliveryListShippingIconSx} />
                <Styled.DriverListRowContent>
                  <Typography variant="caption" color="text.secondary">
                    {t("deliveryman")}
                  </Typography>
                  <Typography variant="subtitle2">{t("undefined")}</Typography>
                </Styled.DriverListRowContent>
              </Styled.DriverListRow>
            )}
          </Styled.DriverList>

          <Button
            variant={
              hasDrivers && !hasMultipleDrivers ? "outlined" : "contained"
            }
            endIcon={<EditIcon />}
            sx={editButtonSx}
            onClick={openEdit}
          >
            {primaryActionLabel.toUpperCase()}
          </Button>
        </Styled.MapAreaViewContainer>
      )}

      <DriverEditCancelConfirmationModal
        open={isCancelModalOpen}
        onContinueEditing={dismissCancelModal}
        onConfirmCancel={confirmCancelEdit}
      />

      <DriverEditSaveConfirmationModal
        open={isSaveModalOpen}
        onDismiss={dismissSaveModal}
        onConfirmSave={confirmSaveEdit}
      />
    </Shared.Container>
  );
}

export default CardDriverAreaMap;
